import {
  ChangeDetectionStrategy, Component, inject, signal, OnInit, PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from '../../../core/services/auth.service';
import { DataService } from '../../../core/services/data.service';
import { CryptoService } from '../../../core/services/crypto.service';
import { ToastService } from '../../../core/services/toast.service';
import { SocialProviderLoginType, AuthResponseModel, SocialMediaLoginRequest } from '../../../core/models/auth.models';
import { ApiResponse } from '../../../core/models/api.models';
import { environment } from '../../../../environments/environment';
import { TrimWhitespaceDirective } from '../../../core/directives/trim.directive';
import { trimFormGroup } from '../../../core/utils/form.utils';

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token?: string; error?: string }) => void;
            error_callback?: (error: unknown) => void;
          }) => { requestAccessToken: (options?: { prompt?: string }) => void };
        };
      };
    };
    FB?: {
      init: (config: { appId: string; cookie: boolean; xfbml: boolean; version: string }) => void;
      login: (
        callback: (response: { authResponse?: { userID?: string } }) => void,
        options?: { scope?: string }
      ) => void;
      api: (
        path: string,
        params: { fields: string },
        callback: (response: { id?: string; name?: string; email?: string; error?: unknown }) => void
      ) => void;
    };
    AppleID?: {
      auth: {
        init: (config: { clientId: string; scope: string; redirectURI: string; usePopup: boolean }) => void;
        signIn: () => Promise<{
          authorization?: { id_token?: string };
          user?: { email?: string; name?: { firstName?: string; lastName?: string } };
        }>;
      };
    };
  }
}

const passwordMatchValidator: ValidatorFn = (control: AbstractControl) => {
  const password = control.get('password');
  const confirmPassword = control.get('confirmPassword');
  if (!password || !confirmPassword) return null;
  return password.value === confirmPassword.value ? null : { passwordMismatch: true };
};

@Component({
  selector: 'app-login',
  templateUrl: './login.html',
  styleUrl: './login.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, RouterLink, TrimWhitespaceDirective],
})
export class Login implements OnInit {
  private fb = inject(FormBuilder);
  private platformId = inject(PLATFORM_ID);
  private authService = inject(AuthService);
  private dataService = inject(DataService);
  private cryptoService = inject(CryptoService);
  private cookieService = inject(CookieService);
  private toastService = inject(ToastService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  readonly activeTab = signal<'signup' | 'signin'>('signup');
  readonly isLoginLoading = signal(false);
  readonly isRegisterLoading = signal(false);
  readonly isGoogleLoading = signal(false);
  readonly isFacebookLoading = signal(false);
  readonly isAppleLoading = signal(false);
  readonly showLoginPassword = signal(false);
  readonly showRegisterPassword = signal(false);
  // readonly showConfirmPassword = signal(false);
  readonly showVerificationBanner = signal(false);
  readonly pendingLoginEmail = signal<string>('');
  readonly isResendLoading = signal(false);

  // Sign In form
  loginForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required],
    rememberMe: [false],
  });

  // Sign Up form
  registerForm = this.fb.group(
    {
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(12),
          Validators.pattern(/^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,12}$/),
        ],
      ],
      // confirmPassword: ['', Validators.required],
    },
    // { validators: passwordMatchValidator }
  );

  ngOnInit(): void {
    // /login route ? show Sign In tab; /register route ? show Sign Up tab (default)
    if (this.route.snapshot.data?.['tab'] === 'signin') {
      this.activeTab.set('signin');
    }
    const saved = this.cookieService.get('fw_credentials');
    if (saved) {
      try {
        const creds = this.cryptoService.doubleDecrypt(saved) as { email: string; password: string; };
        this.loginForm.patchValue({ email: creds.email, password: creds.password, rememberMe: true });
        this.activeTab.set('signin');
      } catch {
        // ignore invalid cookie
      }
    }
  }

  setTab(tab: 'signup' | 'signin'): void { this.activeTab.set(tab); }

  toggleLoginPassword(): void { this.showLoginPassword.update(v => !v); }
  toggleRegisterPassword(): void { this.showRegisterPassword.update(v => !v); }
  // toggleConfirmPassword(): void { this.showConfirmPassword.update(v => !v); }

  get passwordCriteria() {
    const val: string = this.registerForm.get('password')?.value || '';
    return {
      minLength:   val.length >= 8,
      maxLength:   val.length <= 12 && val.length > 0,
      hasUpper:    /[A-Z]/.test(val),
      hasNumber:   /[0-9]/.test(val),
      hasSpecial:  /[@$!%*?&]/.test(val),
    };
  }

  async onSignIn(): Promise<void> {
    trimFormGroup(this.loginForm);
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoginLoading.set(true);
    const { email, password, rememberMe } = this.loginForm.value;

    try {
      const resp = await this.authService.login({
        email: email!,
        password: password!,
      });

      if (resp.status && resp.data) {
        const authData = resp.data as AuthResponseModel;

        // Check if verification is pending
        if (authData.isVerificationPending) {
          this.router.navigate(['/verify-email']);
          return;
        }

        // Handle remember me
        if (rememberMe) {
          const encrypted = this.cryptoService.doubleEncrypt({ email, password }) as string;
          this.cookieService.set('fw_credentials', encrypted, 30, '/');
        } else {
          this.cookieService.delete('fw_credentials');
        }

        // Skip the login toast when redirecting back to an invitation page
        if (!sessionStorage.getItem('fm_pending_invite_token')) {
          this.toastService.success(resp.message || 'Logged in successfully!');
        }
        this.navigateAfterLogin(authData);
      } else if (resp.code === 403) {
        this.toastService.error(resp.message || 'Please verify your email.');
        this.pendingLoginEmail.set(email!);
        this.showVerificationBanner.set(true);
      } else {
        this.toastService.error(resp.message || 'Email or password is incorrect.');
      }
    } catch {
      this.toastService.error('Something went wrong. Please try again.');
    } finally {
      this.isLoginLoading.set(false);
    }
  }

  async onResendVerificationFromLogin(): Promise<void> {
    this.isResendLoading.set(true);
    const resp = await this.authService.resendVerification(this.pendingLoginEmail());
    if (resp.status) {
      this.toastService.success(resp.message);
      this.showVerificationBanner.set(false);
    } else {
      this.toastService.error(resp.message);
    }
    this.isResendLoading.set(false);
  }

  async onSignUp(): Promise<void> {
    trimFormGroup(this.registerForm);
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }

    this.isRegisterLoading.set(true);
    const { firstName, lastName, email, password } = this.registerForm.value;

    const resp = await this.authService.register({
      firstName: firstName!,
      lastName: lastName!,
      email: email!,
      password: password!,
    });

    if (resp.status) {
      this.toastService.success(resp.message);
      this.registerForm.reset();
      this.activeTab.set('signin');
    } else {
      this.toastService.error(resp.message);
    }

    this.isRegisterLoading.set(false);
  }

  /**
   * Social login placeholder - implement with actual provider SDK
   */
  async onGoogleLogin(): Promise<void> {
    if (!this.ensureBrowserContext()) return;

    this.isGoogleLoading.set(true);
    try {
      if (!environment.GOOGLE_CLIENT_ID) {
        this.toastService.error('Google login is not configured.');
        return;
      }

      await this.loadScript('https://accounts.google.com/gsi/client', 'google-gsi-sdk');
      if (!window.google?.accounts?.oauth2) {
        this.toastService.error('Unable to initialize Google login.');
        return;
      }

      const accessToken = await new Promise<string>((resolve, reject) => {
        const tokenClient = window.google!.accounts.oauth2.initTokenClient({
          client_id: environment.GOOGLE_CLIENT_ID,
          scope: 'openid email profile',
          callback: (response) => {
            if (response.error || !response.access_token) {
              reject(new Error('Google authentication failed.'));
              return;
            }
            resolve(response.access_token);
          },
          error_callback: () => reject(new Error('Google authentication failed.')),
        });

        tokenClient.requestAccessToken({ prompt: 'consent' });
      });

      const userInfoResp = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!userInfoResp.ok) {
        this.toastService.error('Unable to fetch Google profile.');
        return;
      }

      const userInfo = (await userInfoResp.json()) as {
        sub?: string;
        name?: string;
        email?: string;
      };

      if (!userInfo.sub || !userInfo.email || !userInfo.name) {
        this.toastService.error('Google account is missing required profile information.');
        return;
      }

      await this.completeSocialLogin({
        socialMediaId: userInfo.sub,
        fullName: userInfo.name,
        email: userInfo.email,
        providerLoginType: SocialProviderLoginType.Google,
      });
    } catch {
      this.toastService.error('Google login failed. Please try again.');
    } finally {
      this.isGoogleLoading.set(false);
    }
  }

  async onFacebookLogin(): Promise<void> {
    if (!this.ensureBrowserContext()) return;

    this.isFacebookLoading.set(true);
    try {
      if (!environment.FACEBOOK_APP_ID) {
        this.toastService.error('Facebook login is not configured.');
        return;
      }

      await this.loadScript('https://connect.facebook.net/en_US/sdk.js', 'facebook-sdk');
      if (!window.FB) {
        this.toastService.error('Unable to initialize Facebook login.');
        return;
      }

      window.FB.init({
        appId: environment.FACEBOOK_APP_ID,
        cookie: true,
        xfbml: false,
        version: 'v19.0',
      });

      const fbUser = await new Promise<{ id: string; name: string; email: string }>((resolve, reject) => {
        window.FB!.login((loginResp) => {
          if (!loginResp.authResponse) {
            reject(new Error('Facebook login cancelled.'));
            return;
          }

          window.FB!.api('/me', { fields: 'id,name,email' }, (profileResp) => {
            if (profileResp.error || !profileResp.id || !profileResp.name || !profileResp.email) {
              reject(new Error('Unable to fetch Facebook profile.'));
              return;
            }
            resolve({
              id: profileResp.id,
              name: profileResp.name,
              email: profileResp.email,
            });
          });
        }, { scope: 'email,public_profile' });
      });

      await this.completeSocialLogin({
        socialMediaId: fbUser.id,
        fullName: fbUser.name,
        email: fbUser.email,
        providerLoginType: SocialProviderLoginType.Facebook,
      });
    } catch {
      this.toastService.error('Facebook login failed. Please try again.');
    } finally {
      this.isFacebookLoading.set(false);
    }
  }

  async onAppleLogin(): Promise<void> {
    if (!this.ensureBrowserContext()) return;

    this.isAppleLoading.set(true);
    try {
      if (!environment.APPLE_CLIENT_ID || !environment.APPLE_REDIRECT_URI) {
        this.toastService.error('Apple login is not configured.');
        return;
      }

      await this.loadScript(
        'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
        'appleid-sdk'
      );

      if (!window.AppleID?.auth) {
        this.toastService.error('Unable to initialize Apple login.');
        return;
      }

      window.AppleID.auth.init({
        clientId: environment.APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: environment.APPLE_REDIRECT_URI,
        usePopup: true,
      });

      const response = await window.AppleID.auth.signIn();
      const socialMediaId = response.authorization?.id_token;
      const email = response.user?.email;
      const name = [response.user?.name?.firstName, response.user?.name?.lastName].filter(Boolean).join(' ');

      if (!socialMediaId || !email || !name) {
        this.toastService.error('Apple did not return full profile information.');
        return;
      }

      await this.completeSocialLogin({
        socialMediaId,
        fullName: name,
        email,
        providerLoginType: SocialProviderLoginType.Apple,
      });
    } catch {
      this.toastService.error('Apple login failed. Please try again.');
    } finally {
      this.isAppleLoading.set(false);
    }
  }

  private ensureBrowserContext(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      return true;
    }
    this.toastService.error('Social login is only available in browser mode.');
    return false;
  }

  private loadScript(src: string, id: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return Promise.resolve();

    const existing = document.getElementById(id) as HTMLScriptElement | null;
    if (existing) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = id;
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  private async completeSocialLogin(data: SocialMediaLoginRequest): Promise<void> {
    const resp = await this.authService.socialMediaLogin(data);

    if (resp.status && resp.data) {
      // Skip the login toast when redirecting back to an invitation page
      if (!sessionStorage.getItem('fm_pending_invite_token')) {
        this.toastService.success(resp.message || 'Logged in successfully!');
      }
      this.navigateAfterLogin(resp.data);
      return;
    }

    if (resp.code === 409) {
      this.toastService.error(
        'This email is associated with a different sign-in method. Please use your original login method.'
      );
      return;
    }

    this.toastService.error(resp.message);
  }

  /** Redirect after successful login based on onboarding flags */
  private navigateAfterLogin(authData: AuthResponseModel): void {
    // If the user arrived via a family-space invitation link (IsNewUser=False),
    // the accept-invitation page saved the tokens before redirecting here.
    // Restore them and return the user to the accept/decline page.
    const pendingToken   = sessionStorage.getItem('fm_pending_invite_token');
    const pendingFsToken = sessionStorage.getItem('fm_pending_invite_fstoken');
    if (pendingToken && pendingFsToken) {
      sessionStorage.removeItem('fm_pending_invite_token');
      sessionStorage.removeItem('fm_pending_invite_fstoken');
      // Tell accept-invitation that this is a post-login return so it does NOT
      // call logoutWithoutRedirect() and immediately undo the just-completed login.
      sessionStorage.setItem('fm_invite_return', '1');
      this.router.navigate(['/accept-invitation'], {
        queryParams: { token: pendingToken, fstoken: pendingFsToken, IsNewUser: 'False' },
      });
      return;
    }

    if (authData.isFamilySpaceAdded === false) {
      this.router.navigate(['/create-family-space']);
    } else {
      this.router.navigate(['/app/stories']);
    }
  }

  get lf() { return this.loginForm.controls; }
  get rf() { return this.registerForm.controls; }
  // get passwordMismatch() { return this.registerForm.errors?.['passwordMismatch'] && this.registerForm.get('confirmPassword')?.touched; }
}

